import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  Target, TrendingUp, ArrowLeft, Loader2, RefreshCw, Activity, BarChart3,
  Crosshair, CircleDot, Layers, Flame,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

const ENGINE_LABELS = {
  'auto-prediction': 'Auto Sócrates',
  'super-ia-master': 'Sócrates Master',
  'super-ia': 'Sócrates',
  'bot-diario': 'Bot Diario',
};

const ENGINE_COLORS = {
  'auto-prediction': '#22d3ee',
  'super-ia-master': '#a78bfa',
  'super-ia': '#f472b6',
  'bot-diario': '#34d399',
};

const MetricGauge = ({ value, label, icon: Icon, color }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2" data-testid={`gauge-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="#1e293b" strokeWidth="6" />
          <circle
            cx="40" cy="40" r={radius} fill="none" stroke={color}
            strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white font-bold text-lg">{value}%</span>
        </div>
      </div>
      <div className="flex items-center gap-1 text-slate-400 text-xs">
        <Icon size={12} />
        <span>{label}</span>
      </div>
    </div>
  );
};

const EngineCard = ({ engine }) => {
  const label = ENGINE_LABELS[engine.engine] || engine.engine;
  const color = ENGINE_COLORS[engine.engine] || '#60a5fa';

  return (
    <div
      className="p-5 rounded-xl bg-slate-900/70 border border-slate-800/50 hover:border-slate-700/60 transition-all"
      data-testid={`engine-${engine.engine}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
          <h3 className="text-white font-bold">{label}</h3>
        </div>
        <Badge variant="outline" className="text-slate-400 border-slate-700 text-xs">
          {engine.total_comparisons} sorteos
        </Badge>
      </div>

      <div className="flex justify-around">
        <MetricGauge value={engine.terminal_match_rate} label="Terminal" icon={CircleDot} color="#22d3ee" />
        <MetricGauge value={engine.neighbor_hit_rate} label="Vecino" icon={Crosshair} color="#f59e0b" />
        <MetricGauge value={engine.exact_match_rate} label="Exacto" icon={Target} color="#10b981" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="bg-slate-800/50 rounded-lg p-2">
          <div className="text-cyan-400 font-bold text-sm">{engine.avg_terminal_per_draw}</div>
          <div className="text-slate-500 text-[10px]">Term/sorteo</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-2">
          <div className="text-amber-400 font-bold text-sm">{engine.avg_neighbor_per_draw}</div>
          <div className="text-slate-500 text-[10px]">Vec/sorteo</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-2">
          <div className="text-emerald-400 font-bold text-sm">{engine.avg_exact_per_draw}</div>
          <div className="text-slate-500 text-[10px]">Exact/sorteo</div>
        </div>
      </div>
    </div>
  );
};

export default function AccuracyDashboard() {
  const { user, token } = useAuth();
  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]);
  const [lotteryData, setLotteryData] = useState([]);
  const [hotAlerts, setHotAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [reportRes, trendRes, lotteryRes, alertsRes] = await Promise.all([
        axios.get(`${API}/accuracy/report?days=${days}`, { headers }),
        axios.get(`${API}/accuracy/daily-trend?days=14`, { headers }),
        axios.get(`${API}/accuracy/by-lottery`, { headers }),
        axios.get(`${API}/accuracy/hot-alerts`, { headers }),
      ]);
      setReport(reportRes.data);
      setTrend(trendRes.data.trend || []);
      setLotteryData(lotteryRes.data.lotteries || []);
      setHotAlerts(alertsRes.data.alerts || []);
    } catch (err) {
      toast.error('Error cargando datos de precision');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [days]);

  const radarData = useMemo(() => {
    if (!report?.engines) return [];
    return report.engines.map(e => ({
      engine: ENGINE_LABELS[e.engine] || e.engine,
      Terminal: e.terminal_match_rate,
      Vecino: e.neighbor_hit_rate,
      Exacto: e.exact_match_rate,
    }));
  }, [report]);

  const topLotteries = useMemo(() => {
    return lotteryData.slice(0, 10).map(l => ({
      name: l.lottery_name.length > 12 ? l.lottery_name.slice(0, 12) + '...' : l.lottery_name,
      terminal: l.terminal_match_rate,
      vecino: l.neighbor_hit_rate,
      exacto: l.exact_match_rate,
    }));
  }, [lotteryData]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <p className="text-slate-400">Inicia sesion para ver la precision</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="accuracy-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft size={16} />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Activity className="text-cyan-400" size={24} />
                Dashboard de Precision
              </h1>
              <p className="text-slate-500 text-sm">Predicciones vs Resultados Reales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-slate-800 text-slate-300 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
              data-testid="days-selector"
            >
              <option value={7}>7 dias</option>
              <option value={14}>14 dias</option>
              <option value={30}>30 dias</option>
              <option value={60}>60 dias</option>
            </select>
            <Button
              variant="outline" size="sm" onClick={fetchData} disabled={loading}
              className="border-slate-700 text-slate-400 hover:text-white"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-cyan-400" size={32} />
          </div>
        ) : (
          <>
            {/* Reference Rates */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-cyan-950/40 to-slate-900/60 border border-cyan-900/30">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-cyan-400" />
                <span className="text-white font-semibold text-sm">Patrones Reales Dominicanos (base de comparacion)</span>
              </div>
              <div className="flex gap-6 text-xs text-slate-400">
                <span>Terminal se repite: <strong className="text-cyan-400">58.3%</strong></span>
                <span>Vecino (+-2) acierta: <strong className="text-amber-400">25.9%</strong></span>
                <span>Repeticion exacta: <strong className="text-emerald-400">8.4%</strong></span>
              </div>
            </div>

            {/* Engine Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="engine-cards">
              {report?.engines?.map((e) => (
                <EngineCard key={e.engine} engine={e} />
              ))}
              {report?.engines?.length === 0 && (
                <div className="col-span-4 text-center py-12 text-slate-500">
                  No hay datos suficientes para el periodo seleccionado.
                  Las predicciones se comparan automaticamente con resultados reales.
                </div>
              )}
            </div>

            {/* Hot Alerts Section */}
            {hotAlerts.length > 0 && (
              <div className="mb-8 p-5 rounded-xl bg-gradient-to-r from-orange-950/30 to-slate-900/60 border border-orange-900/30" data-testid="hot-alerts-section">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <Flame className="text-orange-400" size={18} />
                  Loterias Calientes — Sócrates con Alta Precision
                  <Badge variant="outline" className="text-orange-400 border-orange-500/40 ml-auto text-xs">
                    {hotAlerts.length} alertas
                  </Badge>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {hotAlerts.slice(0, 8).map((alert) => (
                    <div
                      key={alert.lottery_id}
                      className={`p-3 rounded-lg border transition-all ${
                        alert.level === 'hot'
                          ? 'bg-orange-500/10 border-orange-500/30 hover:border-orange-400/50'
                          : 'bg-amber-500/10 border-amber-500/20 hover:border-amber-400/40'
                      }`}
                      data-testid={`hot-alert-${alert.lottery_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Flame size={14} className={alert.level === 'hot' ? 'text-orange-400' : 'text-amber-400'} />
                        <span className="text-white text-sm font-bold truncate">{alert.lottery_name}</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap mb-2">
                        {alert.reasons.map((r, i) => (
                          <span
                            key={i}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              r.includes('Terminal') ? 'bg-cyan-500/20 text-cyan-300' :
                              r.includes('Vecino') ? 'bg-amber-500/20 text-amber-300' :
                              'bg-emerald-500/20 text-emerald-300'
                            }`}
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {alert.total_comparisons} sorteos analizados
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Daily Trend */}
              <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800/50" data-testid="trend-chart">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <TrendingUp size={16} className="text-cyan-400" />
                  Tendencia Diaria
                </h3>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }}
                        tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 10 }} domain={[0, 100]} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                        labelStyle={{ color: '#94a3b8' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="terminal_rate" name="Terminal %"
                        stroke="#22d3ee" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="neighbor_rate" name="Vecino %"
                        stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="exact_rate" name="Exacto %"
                        stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-600 text-sm">
                    Sin datos de tendencia
                  </div>
                )}
              </div>

              {/* By Lottery */}
              <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800/50" data-testid="lottery-chart">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <BarChart3 size={16} className="text-amber-400" />
                  Precision por Loteria (Top 10)
                </h3>
                {topLotteries.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={topLotteries} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" width={100}
                        tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                      />
                      <Bar dataKey="terminal" name="Terminal %" fill="#22d3ee" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="vecino" name="Vecino %" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-600 text-sm">
                    Sin datos por loteria
                  </div>
                )}
              </div>
            </div>

            {/* Lottery Detail Table */}
            {lotteryData.length > 0 && (
              <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800/50" data-testid="lottery-table">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Target size={16} className="text-emerald-400" />
                  Detalle por Loteria
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left text-slate-400 py-2 px-3">Loteria</th>
                        <th className="text-center text-slate-400 py-2 px-3">Sorteos</th>
                        <th className="text-center text-cyan-400 py-2 px-3">Terminal %</th>
                        <th className="text-center text-amber-400 py-2 px-3">Vecino %</th>
                        <th className="text-center text-emerald-400 py-2 px-3">Exacto %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotteryData.map((l, i) => (
                        <tr key={l.lottery_id} className="border-b border-slate-800/40 hover:bg-slate-800/30">
                          <td className="py-2 px-3 text-white font-medium">{l.lottery_name}</td>
                          <td className="py-2 px-3 text-center text-slate-400">{l.total_comparisons}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`font-bold ${l.terminal_match_rate >= 58 ? 'text-cyan-400' : 'text-slate-400'}`}>
                              {l.terminal_match_rate}%
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`font-bold ${l.neighbor_hit_rate >= 25 ? 'text-amber-400' : 'text-slate-400'}`}>
                              {l.neighbor_hit_rate}%
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`font-bold ${l.exact_match_rate >= 8 ? 'text-emerald-400' : 'text-slate-400'}`}>
                              {l.exact_match_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
